import { Directive, HostListener } from '@angular/core';

/**
 * Global directive that prevents backslash (\) and double quote (") from being
 * typed or pasted into any text input or textarea.
 *
 * Works on both desktop (keydown) and mobile (input event fallback).
 *
 * Applied automatically to all <input> and <textarea> elements
 * (excluding checkboxes, radios, and other non-text inputs).
 */
@Directive({
  selector: `
    input:not([type=checkbox]):not([type=radio]):not([type=file]):not([type=button]):not([type=submit]):not([type=reset]),
    textarea
  `
})
export class NoSpecialCharsDirective {

  private readonly BLOCKED_KEYS = ['\\', '"'];
  private readonly BLOCKED_PATTERN = /[\\\"]/g;

  /**
   * Desktop: block the key before it is inserted, based on the actual character
   * produced (event.key) — NOT keyCode, which is layout-independent (physical key position).
   *
   * Example: keyCode 220 = backslash on English layout, but = 'ظ' on Arabic layout.
   * Checking event.key ensures Arabic users can still type 'ظ' freely.
   *
   * On mobile, key is 'Unidentified', so this is a no-op — the input event handles mobile.
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.BLOCKED_KEYS.includes(keyboardEvent.key)) {
      keyboardEvent.preventDefault();
    }
  }

  /**
   * Mobile + Desktop fallback: strip forbidden characters AFTER they are inserted.
   * This is the primary guard on mobile keyboards where keydown is unreliable.
   */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = input.value;

    if (!this.BLOCKED_PATTERN.test(value)) return;

    // Reset the regex lastIndex after test()
    this.BLOCKED_PATTERN.lastIndex = 0;

    const start = input.selectionStart ?? 0;
    const cleanedValue = value.replace(this.BLOCKED_PATTERN, '');

    // Calculate how many blocked chars were removed before the cursor
    const removedBeforeCursor = (value.substring(0, start).match(this.BLOCKED_PATTERN) ?? []).length;

    input.value = cleanedValue;

    // Restore cursor position accounting for removed characters
    const newCursor = start - removedBeforeCursor;
    input.setSelectionRange(newCursor, newCursor);

    // Notify Angular's change detection
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Paste: intercept and strip forbidden characters from pasted text.
   * Works reliably on both desktop and mobile.
   */
  @HostListener('paste', ['$event'])
  onPaste(event: Event): void {
    const clipboardData = (event as ClipboardEvent).clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text');

    if (!this.BLOCKED_PATTERN.test(pastedText)) {
      this.BLOCKED_PATTERN.lastIndex = 0;
      return;
    }

    this.BLOCKED_PATTERN.lastIndex = 0;
    event.preventDefault();

    // Strip forbidden chars and insert cleaned text at cursor position
    const cleanedText = pastedText.replace(this.BLOCKED_PATTERN, '');
    this.BLOCKED_PATTERN.lastIndex = 0;

    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    input.value = input.value.substring(0, start) + cleanedText + input.value.substring(end);

    // Notify Angular's change detection
    input.dispatchEvent(new Event('input', { bubbles: true }));

    // Restore cursor position after inserted text
    input.setSelectionRange(start + cleanedText.length, start + cleanedText.length);
  }

   @HostListener('wheel', ['$event'])
  onWheel(event: Event): void {
    const input = event.target as HTMLInputElement ;
    event.preventDefault();
  } 
}